import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import VerificationInput from "react-verification-input";
import AccountService from "../../Services/Account/AccountService";
import { setWebInfo } from "../../Redux/Actions/Index";
import { cacheAuthenticatorEnabled } from "./authenticatorStatusCache";
import Commonservice from "Services/CommonService";
import LoadButton from "Shared/Common/LoadButton";
import { Loader } from "Shared/Common/Loader";
import useAuth from "Shared/hooks/useAuth";
import useLang from "Shared/hooks/useLanguage";
import {
  setFaviconAndTitle,
  setValueIntoSessionStorage,
} from "Utils/Common/CommonMethods";

const PAGE_ID = 18;

type SetupStep = "qr" | "otp" | "password" | "success";

interface AuthenticatorSetup {
  succeeded: boolean;
  statusCode: number;
  message: string;
  setupKey: string;
  formattedSetupKey: string;
  qrCodeBase64: string;
  otpAuthUri: string;
  issuer: string;
  accountName: string;
  setupExpiresAt: string;
  lockedUntil: string;
}

const pageShellStyle = {
  backgroundImage: 'url("/media/illustrations/login/sketch-1.png")',
} as const;

const EnableAuthenticator: React.FC = () => {
  const { t } = useLang();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { navigateToLandingPage } = useAuth();

  const [step, setStep] = useState<SetupStep>("qr");
  const [setup, setSetup] = useState<AuthenticatorSetup | null>(null);
  const [loading, setLoading] = useState(true);
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const verificationInputRef = useRef<any>(null);

  const syncWebInfo = () => {
    Commonservice.getLoginPageLogo()
      .then((res: any) => {
        const data = res?.data;
        if (data && typeof data !== "string") {
          dispatch(
            setWebInfo({
              smartLogoUrl: data?.smartLogo,
              title: data?.labName,
            })
          );
          setFaviconAndTitle(data?.smartLogo, data?.labName);
        }
      })
      .catch((err: any) => {
        console.trace(err);
      });
  };

  const startSetup = async () => {
    try {
      setLoading(true);
      const res = await AccountService.startAuthenticatorSetup();
      const payload = res?.data;
      if (payload?.succeeded || payload?.isSuccess) {
        setSetup(payload?.data || payload);
        setStep("qr");
      } else {
        toast.error(
          payload?.message || t("Failed to start authenticator setup")
        );
      }
    } catch (err: any) {
      console.trace(err);
      toast.error(
        err?.response?.data?.message || t("Failed to start authenticator setup")
      );
    } finally {
      setLoading(false);
    }
  };

  const copySetupKey = async () => {
    const key = setup?.formattedSetupKey || setup?.setupKey || "";
    if (!key) return;
    try {
      await navigator.clipboard.writeText(key.replace(/\s/g, ""));
      toast.success(t("Copied to clipboard"));
    } catch {
      toast.error(t("Failed to copy"));
    }
  };

  const getQrImageSrc = () => {
    const qr = setup?.qrCodeBase64;
    if (!qr) return "";
    return qr.startsWith("data:") ? qr : `data:image/png;base64,${qr}`;
  };

  const handleVerifyOtp = async () => {
    if (otp.length !== 6) {
      toast.error(t("Please enter a valid 6-digit code"));
      return;
    }

    try {
      setSubmitting(true);
      const res = await AccountService.verifyAuthenticatorSetupCode(otp);
      const payload = res?.data;
      if (
        payload?.succeeded ||
        payload?.isSuccess ||
        payload?.statusCode === 200
      ) {
        toast.success(
          payload?.message || t("Authenticator verified successfully")
        );
        setPassword("");
        setShowPassword(false);
        setStep("password");
      } else {
        toast.error(payload?.message || t("Invalid verification code"));
        setOtp("");
      }
    } catch (err: any) {
      console.trace(err);
      toast.error(
        err?.response?.data?.message || t("Failed to verify authenticator code")
      );
      setOtp("");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCompleteSetup = async () => {
    if (!password.trim()) {
      toast.error(t("Password is required"));
      return;
    }

    try {
      setSubmitting(true);
      const res = await AccountService.completeAuthenticatorSetup(password);
      const payload = res?.data;
      if (
        payload?.succeeded ||
        payload?.isSuccess ||
        payload?.statusCode === 200
      ) {
        toast.success(
          payload?.message || t("Authenticator setup completed successfully")
        );
        cacheAuthenticatorEnabled(true);
        setStep("success");
      } else {
        toast.error(payload?.message || t("Invalid password"));
      }
    } catch (err: any) {
      console.trace(err);
      toast.error(
        err?.response?.data?.message || t("Failed to verify password")
      );
    } finally {
      setSubmitting(false);
    }
  };

  const goToSettings = () => navigate("/authenticator-settings");

  const handleCancelAnyway = () => {
    setShowCancelModal(false);
    goToSettings();
  };

  useLayoutEffect(() => {
    setValueIntoSessionStorage("pageId", PAGE_ID);
    syncWebInfo();
  }, []);

  useEffect(() => {
    startSetup();
  }, []);

  useEffect(() => {
    if (step === "otp") {
      verificationInputRef.current?.focus();
    }
  }, [step]);

  const displayKey = setup?.formattedSetupKey || setup?.setupKey || "";

  const renderQrStep = () => (
    <>
      <div className="text-center mb-8">
        <h3 className="mb-0">{t("Enable Authenticator")}</h3>
      </div>
      {loading ? (
        <div className="d-flex justify-content-center py-4">
          <Loader />
        </div>
      ) : (
        <>
          <p className="text-center text-gray-700 mb-6">
            {t("Scan this QR code with your Authenticator app")}
          </p>
          <div className="d-flex justify-content-center mb-6">
            {getQrImageSrc() ? (
              <img
                alt={t("Authenticator QR Code")}
                src={getQrImageSrc()}
                style={{ width: 220, height: 220 }}
              />
            ) : (
              <span className="text-muted">{t("QR code not available")}</span>
            )}
          </div>
          <p className="text-center text-gray-700 mb-4">
            {t(
              "If your app does not recognize QR code, enter the following code manually"
            )}
          </p>
          <div className="d-flex align-items-center gap-2 mb-10">
            <input
              type="text"
              className="form-control"
              value={displayKey}
              readOnly
              data-test-id="authenticator-setup-key"
            />
            <button
              type="button"
              className="btn btn-light-primary h-40px"
              onClick={copySetupKey}
              data-test-id="copy-authenticator-setup-key"
              title={t("Copy")}
            >
              <i className="bi bi-clipboard fs-4"></i>
            </button>
          </div>
          <div className="d-flex justify-content-center align-items-center gap-4">
            <button
              type="button"
              className="btn btn-primary h-45px"
              style={{ width: "max-content", whiteSpace: "nowrap" }}
              onClick={() => {
                setOtp("");
                setStep("otp");
              }}
              data-test-id="enable-authenticator-continue"
            >
              {t("Continue")}
            </button>
            <button
              type="button"
              className="btn btn-secondary h-45px"
              style={{ width: "max-content", whiteSpace: "nowrap" }}
              onClick={goToSettings}
              data-test-id="enable-authenticator-cancel"
            >
              {t("Cancel")}
            </button>
          </div>
        </>
      )}
    </>
  );

  const renderOtpStep = () => (
    <>
      <div className="text-center mb-8">
        <h3 className="mb-3">{t("OTP Verification")}</h3>
        <p className="text-gray-700 mb-0">
          {t("Please enter the 6-digit code from your Authenticator app")}
        </p>
      </div>
      <div className="fv-row mb-10 d-flex justify-content-center">
        <VerificationInput
          ref={verificationInputRef}
          autoFocus
          length={6}
          validChars="0-9"
          placeholder=""
          value={otp}
          onChange={(val: string) => setOtp(val)}
          inputProps={{ inputMode: "numeric" }}
          classNames={{
            container: "d-flex gap-2",
            character: "form-control form-control-lg text-center",
            characterInactive: "opacity-50",
          }}
        />
      </div>
      <div className="d-flex justify-content-center align-items-center gap-4">
        <LoadButton
          id="verify-authenticator-submit"
          className="btn btn-primary h-45px"
          style={{ width: "max-content", whiteSpace: "nowrap" }}
          loading={submitting}
          btnText={t("Submit")}
          loadingText={t("Verifying")}
          onClick={handleVerifyOtp}
          disabled={otp.length !== 6}
        />
        <button
          type="button"
          className="btn btn-secondary h-45px"
          style={{ width: "max-content", whiteSpace: "nowrap" }}
          onClick={() => setStep("qr")}
          data-test-id="verify-authenticator-cancel"
        >
          {t("Cancel")}
        </button>
      </div>
    </>
  );

  const renderPasswordStep = () => (
    <>
      <div className="text-center mb-8">
        <h3 className="mb-3">{t("Password Verification")}</h3>
        <p className="text-gray-700 mb-0">
          {t("Please enter your password to complete authenticator setup")}
        </p>
      </div>
      <div className="fv-row mb-10">
        <div className="d-flex">
          <input
            type={showPassword ? "text" : "password"}
            className="form-control form-control-lg"
            placeholder={t("Password")}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-test-id="verify-authenticator-password"
            onKeyDown={(e) => {
              if (e.key === "Enter") handleCompleteSetup();
            }}
          />
          <button
            type="button"
            className="btn btn-outline-primary"
            onClick={() => setShowPassword((prev) => !prev)}
            data-test-id="toggle-authenticator-password"
          >
            <i className={`bi ${showPassword ? "bi-eye-slash" : "bi-eye"}`}></i>
          </button>
        </div>
      </div>
      <div className="d-flex justify-content-center align-items-center gap-4">
        <LoadButton
          id="verify-authenticator-password-submit"
          className="btn btn-primary h-45px"
          style={{ width: "max-content", whiteSpace: "nowrap" }}
          loading={submitting}
          btnText={t("Submit")}
          loadingText={t("Verifying")}
          onClick={handleCompleteSetup}
          disabled={!password.trim()}
        />
        <button
          type="button"
          className="btn btn-secondary h-45px"
          style={{ width: "max-content", whiteSpace: "nowrap" }}
          onClick={() => setShowCancelModal(true)}
          data-test-id="verify-authenticator-password-cancel"
        >
          {t("Cancel")}
        </button>
      </div>
    </>
  );

  const renderSuccessStep = () => (
    <div className="text-center">
      <div className="mb-6">
        <i
          className="fa-regular fa-circle-check text-success"
          style={{ fontSize: "8.5rem" }}
          aria-hidden
        />
      </div>
      <p className="text-gray-800 fs-4 mb-10">
        {t("Your account is now secured with your authenticator app.")}
      </p>
      <button
        type="button"
        className="btn btn-primary h-45px"
        style={{ width: "max-content", whiteSpace: "nowrap" }}
        onClick={() => navigateToLandingPage()}
        data-test-id="authenticator-success-done"
      >
        {t("Done")}
      </button>
    </div>
  );

  return (
    <div
      className="d-flex flex-column flex-column-fluid bgi-position-y-bottom position-x-center bgi-no-repeat bgi-size-contain bgi-attachment-fixed"
      style={pageShellStyle}
    >
      <div
        className="d-flex flex-center flex-column flex-column-fluid p-10 pb-lg-20"
        style={{ minHeight: "100vh" }}
      >
        <div className="w-lg-600px bg-body rounded shadow-sm p-10 p-lg-15 mx-auto">
          {step === "qr" && renderQrStep()}
          {step === "otp" && renderOtpStep()}
          {step === "password" && renderPasswordStep()}
          {step === "success" && renderSuccessStep()}
        </div>
      </div>

      <Modal
        show={showCancelModal}
        onHide={() => setShowCancelModal(false)}
        backdrop="static"
      >
        <Modal.Body className="text-center p-8">
          <p className="fs-5 text-gray-800 mb-8">
            {t(
              "Authenticator setup is not complete. Are you sure you want to cancel?"
            )}
          </p>
          <div className="d-flex justify-content-center align-items-center gap-4 flex-wrap">
            <button
              type="button"
              className="btn btn-danger h-45px"
              style={{ width: "max-content", whiteSpace: "nowrap" }}
              onClick={handleCancelAnyway}
              data-test-id="cancel-authenticator-setup-anyway"
            >
              {t("Cancel anyway")}
            </button>
            <button
              type="button"
              className="btn btn-primary h-45px"
              style={{ width: "max-content", whiteSpace: "nowrap" }}
              onClick={() => setShowCancelModal(false)}
              data-test-id="continue-authenticator-setup"
            >
              {t("Continue setup")}
            </button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default EnableAuthenticator;
